<template>
  <VLoader v-if="tableLoading" classes="bg-white" />
  <div v-else>
    <div class="shared-table-container">
      <div v-if="withToolbar" class="shared-table-toolbar">
        <h3 class="shared-table-toolbar-heading">{{ toolbarHeading }}</h3>

        <!-- Filter by text - Desktop view -->
        <form
          v-if="withSearch"
          @submit.prevent=""
          class="hidden shared-table-toolbar-search lg:block"
        >
          <VSvgIcon name="magnify" class="shared-table-toolbar-search-icon" />
          <input
            type="text"
            :placeholder="searchPlaceholder"
            class="shared-table-toolbar-search-input"
            v-model="searchText"
          />
        </form>

        <!-- Filter by date range - Desktop view  -->
        <div v-if="withDateRange" class="hidden lg:block">
          <TooltipDateRange @onSelect="handleDateRangeSelect" />
        </div>

        <!-- Custom components -->
        <div class="flex flex-wrap items-center justify-center w-full ml-auto sm:w-auto">
          <slot name="table-toolbar"></slot>
        </div>

        <!-- Filter by text - Mobile view -->
        <form
          v-if="withSearch"
          @submit.prevent=""
          class="w-full my-3 mr-0 shared-table-toolbar-search lg:hidden"
        >
          <VSvgIcon name="magnify" class="shared-table-toolbar-search-icon" />
          <input
            type="text"
            :placeholder="searchPlaceholder"
            class="shared-table-toolbar-search-input"
            v-model="searchText"
          />
        </form>

        <!-- Filter by date range - Mobile view  -->
        <div v-if="withDateRange" class="block w-auto mx-auto my-3 lg:hidden">
          <TooltipDateRange @onSelect="handleDateRangeSelect" />
        </div>
      </div>

      <div class="shared-table-innermost-container">
        <table class="shared-table">
          <thead>
            <tr class="shared-table-headings">
              <th
                v-for="({
                  column,
                  columnDisplay = null,
                  isSort = true,
                  classes = { th: '', td: '' },
                  orderBy = '',
                },
                thIndex) in tColumns"
                :key="`heading-${thIndex}`"
                class="shared-table-headings-heading"
                :class="[isSort && 'cursor-pointer', ...classes.th]"
                @click="handleSortByHeader({ isSort, orderBy })"
              >
                <!-- Fallback to simple default th content -->
                <div class="flex items-center justify-center" :class="[!isSort && 'cursor-auto']">
                  <slot
                    :name="`table-heading-${thIndex}`"
                    v-bind="{ heading: columnDisplay ? columnDisplay : column }"
                  >
                    {{ columnDisplay ? columnDisplay : column }}
                    <VSvgIcon v-if="isSort" name="swap-vertical" class="h-4" />
                  </slot>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- #1 Show on empty table -->
            <template v-if="!tableData.data.length">
              <tr class="shared-table-cells" :style="{ ...styles.tr }">
                <td :colspan="tColumns.length" class="shared-table-cells-cell">
                  <div class="py-10 text-center uppercase">
                    {{ toolbarHeading ? `-- the ${toolbarHeading} is empty --` : `-- empty --` }}
                  </div>
                </td>
              </tr>
            </template>

            <!-- #2 Shows when there's data -->
            <template v-else>
              <tr
                v-for="(trData, trIndex) in tableData.data"
                :key="`tr-data-${trIndex}`"
                class="shared-table-cells"
                :class="[...propsClasses.tr]"
                :style="{ ...propsStyles.tr }"
              >
                <!-- #2 - A - Show when we want a single td to span entire tr -->
                <template v-if="isSingleColspan(trData)">
                  <td :colspan="tColumns.length" class="shared-table-cells-cell">
                    <slot
                      :name="`table-data-single`"
                      v-bind="{
                        ...trData,
                        trIndex,
                        tableLength: tableData.data.length,
                        columnsLength: tColumns.length,
                      }"
                    >
                    </slot>
                  </td>
                </template>

                <!-- #2 - B - Show when we want each td to span a column -->
                <template v-else-if="trData.type !== 'STATEMENT' && trData.type !== 'PAYMENT'">
                  <td
                    v-for="(tdData, tdIndex) in Object.keys(trData).filter(trD =>
                      tColumns.some(({ column }) => trD === column),
                    )"
                    :key="`td-data-${tdIndex}`"
                    class="shared-table-cells-cell"
                    :class="[...setTableTdClass(tColumns, tdData), ...propsClasses['td'][tdIndex]]"
                    :style="{ ...propsStyles['td'][tdIndex] }"
                  >
                    <slot
                      :name="`table-data-${tdIndex}`"
                      v-bind="{
                        ...trData,
                        trIndex,
                        tableLength: tableData.data.length,
                        columnsLength: tColumns.length,
                      }"
                    >
                      {{ trData[tdData] }}
                    </slot>
                  </td>
                </template>

                <slot name="table-data-actions" v-bind="trData"></slot>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="withPagination" class="flex items-center justify-center">
      <Pagination
        :totalPages="
          tableData.meta && tableData.meta.pagination ? tableData.meta.pagination.total_pages : 1
        "
        :currentPage="
          tableData.meta && tableData.meta.pagination ? tableData.meta.pagination.current_page : 1
        "
        @onPaginate="handlePaginate"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import { debounce, typeCheck } from '@/utils';

import VLoader from '@/components/base/VLoader';
import VSvgIcon from '@/components/base/VSvgIcon';
import Pagination from '@/components/shared/Pagination';
import TooltipDateRange from '@/components/shared/tooltip/TooltipDateRange';

export default {
  name: 'Table',
  components: {
    VSvgIcon,
    VLoader,
    Pagination,
    TooltipDateRange,
  },
  props: {
    tColumns: {
      type: Array,
      required: false,
      default: () => [],
    },
    tData: {
      type: [Object, Array],
      required: false,
      default: () => ({}),
    },
    fetch: {
      type: Function,
      required: false,
      default: () => {},
    },
    toolbarHeading: {
      type: String,
      required: false,
      default: '',
    },
    withToolbar: {
      type: Boolean,
      required: false,
      default: false,
    },
    withSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    withPagination: {
      type: Boolean,
      required: false,
      default: false,
    },
    withTranslationColumn: {
      type: Boolean,
      required: false,
      default: false,
    },
    withDateRange: {
      type: Boolean,
      required: false,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      required: false,
      default: 'search...',
    },
    isSingleColspan: {
      type: Function,
      required: false,
      default: () => false,
    },
    classes: {
      type: Object,
      requird: false,
      default: () => ({ tr: '', td: [] }),
    },
    styles: {
      type: Object,
      requird: false,
      default: () => ({ tr: {}, td: [] }),
    },
  },
  data() {
    return {
      dateRanges: ['', ''],
      sortAscending: true,
      searchText: '',
      tableLoading: false,
    };
  },
  computed: mapState({
    tableData() {
      if (typeCheck(this.tData).isArray) return { data: this.tData, meta: null };
      return this.tData;
    },
    language: state => state.language,
    propsStyles() {
      const tr = this.styles.tr ?? '';
      const td = this.styles.td ?? [];
      return { tr, td };
    },
    propsClasses() {
      const tr = this.classes.tr ?? '';
      const td = this.classes.td ?? [];
      return { tr, td };
    },
  }),
  watch: {
    async language({ code: lang_code }) {
      return await this.fetchData({ lang_code });
    },
    searchText: debounce(async function (text) {
      const { code: lang_code } = this.language;
      const params = { search: text, lang_code };
      return await this.fetchData(params);
    }, 500),
  },
  methods: {
    setTableTdClass(tColumns, tdData) {
      const column = tColumns.find(({ column }) => column === tdData);
      return column.classes ? column.classes.td : '';
    },
    fetchData(params) {
      const { code: lang_code = 'en' } = this.language;
      const parameters = { lang_code, ...params };
      return this.fetch(parameters);
    },
    async handlePaginate(page) {
      const start_date = this.dateRanges[0];
      const end_date = this.dateRanges[1];

      const params = !(start_date && end_date) ? { page } : { page, start_date, end_date };
      await this.fetchData(params);
    },
    async handleDateRangeSelect(dateRanges) {
      const start_date = dateRanges[0];
      const end_date = dateRanges[1];
      const params = !(start_date && end_date) ? {} : { start_date, end_date };

      await this.fetchData(params);
      this.dateRanges = dateRanges;
    },
    async handleSortByHeader({ isSort, orderBy }) {
      const orderDir = this.sortAscending ? 'asc' : 'desc';
      this.sortAscending = !this.sortAscending;
      if (isSort) await this.fetchData({ order_by: orderBy, order_dir: orderDir });
    },
  },
  async mounted() {
    this.tableLoading = true;
    if (!this.tableData.data.length) await this.fetchData();
    this.tableLoading = false;
  },
};
</script>
